/**
 * WelcomeBanner — Time-aware unified welcome card for the dashboard.
 * Combines logo mark, time-of-day eyebrow, friendly salutation, and rotating micro-copy
 * over a tone gradient (provided by an ancestor `.tone-*` class). Designed to flow
 * visually into the TodayHeroCard below.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { format } from 'date-fns';
import { Sunrise, Sun, SunMedium, Moon, ChevronUp, ChevronDown } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { useUserProfile } from '@/hooks/useUserProfile';
import { cn } from '@/lib/utils';
import {
  TimeSlot,
  getTimeSlot,
  getEyebrow,
  pickMicrocopy,
  resolveFirstName,
} from '@/lib/timeOfDay';
import { logoIcon } from '@/components/brand/LogoExports';
import { CheckInChip } from '@/components/dashboard/CheckInChip';
import { usePoints } from '@/hooks/usePoints';
import { StatusStrip } from '@/components/gamification/StatusStrip';

interface WelcomeBannerProps {
  user: User | null;
}

const ICONS: Record<TimeSlot, typeof Sun> = {
  morning: Sunrise,
  afternoon: Sun,
  evening: SunMedium,
  night: Moon,
};

export function WelcomeBanner({ user }: WelcomeBannerProps) {
  const { profile } = useUserProfile();
  const { points, level } = usePoints();
  const reduce = useReducedMotion();
  const [userCollapsed, setUserCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('welcomeBannerCollapsed') === '1';
  });
  const [scrollCollapsed, setScrollCollapsed] = useState(false);
  const collapsed = userCollapsed || scrollCollapsed;

  useEffect(() => {
    const onScroll = () => setScrollCollapsed(window.scrollY > 200);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleCollapsed = () => {
    setUserCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('welcomeBannerCollapsed', next ? '1' : '0');
      } catch {}
      return next;
    });
  };

  const { slot, eyebrow, microcopy, dateLabel, name, Icon } = useMemo(() => {
    const now = new Date();
    const s = getTimeSlot(now);
    return {
      slot: s,
      eyebrow: getEyebrow(s),
      microcopy: pickMicrocopy(s, now),
      dateLabel: format(now, 'EEE · d MMM'),
      name: user ? resolveFirstName(profile?.display_name, user.email) : null,
      Icon: ICONS[s],
    };
  }, [user, profile?.display_name]);


  const salutation = name ? `Hello, ${name}` : 'Welcome to GoalDigger';

  return (
    <section
      aria-label="Welcome"
      className={cn(
        "gd-hero-deck transition-all duration-300 ease-out",
        collapsed && "rounded-[22px]"
      )}
    >
      {/* Ambient highlights */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full bg-primary/30 blur-3xl"
        animate={reduce ? undefined : { x: [0, 14, 0], y: [0, -10, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-primary-glow/20 blur-3xl"
        animate={reduce ? undefined : { x: [0, -12, 0], y: [0, 12, 0] }}
        transition={{ duration: 13, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Golden-hour dune glow across the full masthead */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,hsl(var(--primary)/0.28),transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <div className={cn(
        "relative flex items-start gap-4 sm:gap-5 lg:gap-8 transition-all duration-300 ease-out",
        collapsed ? "p-3 sm:p-3.5 items-center" : "p-6 sm:p-10 lg:px-14 lg:py-14 xl:py-16"
      )}>
        {/* Glassy logo badge */}
        <div className="shrink-0">
          <div className={cn(
            "relative rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 shadow-[inset_0_1px_0_hsl(0_0%_100%/0.4)] flex items-center justify-center transition-all duration-300",
            collapsed ? "h-9 w-9" : "h-14 w-14 sm:h-16 sm:w-16"
          )}>
            <img
              src={logoIcon}
              alt="GoalDigger"
              className={cn(
                "object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)] transition-all duration-300",
                collapsed ? "h-6 w-6" : "h-9 w-9 sm:h-10 sm:w-10"
              )}
            />
          </div>
        </div>

        {/* Greeting block */}
        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          {!collapsed && (
            <div className="inline-flex items-center gap-2 text-white/95">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/25 text-primary-glow border border-primary/40 px-2.5 py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] drop-shadow-sm">
                <Icon className="h-3.5 w-3.5" />
                {eyebrow}
              </span>
              <span className="text-[11px] sm:text-xs font-medium text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                {dateLabel}
              </span>
            </div>
          )}

          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className={cn(
              "gd-display text-white transition-all duration-300",
              collapsed ? "text-base sm:text-lg" : "text-3xl sm:text-5xl"
            )}
          >
            {salutation}
          </motion.h1>

          {!collapsed && (
            <p className="text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed max-w-prose drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
              {microcopy}
            </p>
          )}

          {/* Integrated status tray — points, claim, redeem live inside the masthead */}
          {!collapsed && user && (
            <div className="mt-4 lg:mt-6 max-w-xl rounded-full border border-white/10 bg-white/[0.05] p-1.5 backdrop-blur-md shadow-[inset_0_1px_0_hsl(0_0%_100%/0.12)]">
              <StatusStrip />
            </div>
          )}
        </div>

        {/* Right wing — editorial status medallion (date + points + level) */}
        {!collapsed && (
          <div className="hidden md:flex shrink-0 self-end items-stretch gap-0 rounded-[26px] border border-white/15 bg-white/[0.06] backdrop-blur-md shadow-[inset_0_1px_0_hsl(0_0%_100%/0.18),0_24px_48px_-24px_hsl(0_0%_0%/0.6)] mt-10 overflow-hidden">
            <div className="flex flex-col items-center justify-center px-5 py-4 border-r border-white/10">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary-glow">{format(new Date(), 'MMM')}</span>
              <span className="gd-numeral text-4xl lg:text-5xl font-bold text-white">{format(new Date(), 'd')}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">{format(new Date(), 'EEE')}</span>
            </div>
            {user ? (
              <div className="flex items-center gap-4 px-5 py-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-glow to-primary flex items-center justify-center shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.7)]">
                  <span className="font-outfit text-sm font-bold text-primary-foreground">L{level}</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">Your points</span>
                  <span className="font-outfit text-2xl lg:text-3xl font-bold tabular-nums text-white">{points.toLocaleString()}</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col justify-center px-5 py-4 max-w-[200px]">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">Today in the UAE</span>
                <span className="font-outfit text-base font-semibold text-white leading-snug">Save a place, plan a weekend.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Top-right controls: daily check-in + collapse toggle */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        {!collapsed && user && <CheckInChip />}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand welcome banner' : 'Collapse welcome banner'}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white transition-colors"
        >
          {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Hairline that hands off to the hero card visually */}

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
      />
    </section>
  );
}
